import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { PayrollSettings } from '../../types/finances';
import { positionMonthlyCost } from '../../utils/financesForecast';
import { US_STATE_CODES } from '../../utils/payrollEmployerTax';
import PayrollCostBreakdown from './PayrollCostBreakdown';

interface PayrollSettingsModalProps {
  initial: PayrollSettings;
  onClose: () => void;
  onSave: (next: Partial<PayrollSettings>) => Promise<void>;
}

const PayrollSettingsModal: React.FC<PayrollSettingsModalProps> = ({ initial, onClose, onSave }) => {
  const [sepPct, setSepPct] = useState<string>(((initial.sepIraRate ?? 0) * 100).toFixed(2));
  const [taxPct, setTaxPct] = useState<string>(((initial.employerTaxRate ?? 0) * 100).toFixed(2));
  const [ptoDays, setPtoDays] = useState<string>(String(initial.defaultPtoDaysPerYear ?? 10));
  const [healthDefault, setHealthDefault] = useState<string>(
    String(initial.defaultHealthReimbursementMonthly ?? 0),
  );
  const [extrasDefault, setExtrasDefault] = useState<string>(
    String(initial.defaultMonthlyEmployeeExtras ?? 0),
  );
  const [defaultWorkState, setDefaultWorkState] = useState<string>(
    initial.defaultWorkState ?? 'VA',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const numericSep = useMemo(() => {
    const v = parseFloat(sepPct);
    return Number.isFinite(v) ? Math.max(0, v) / 100 : 0;
  }, [sepPct]);

  const numericTax = useMemo(() => {
    const v = parseFloat(taxPct);
    return Number.isFinite(v) ? Math.max(0, v) / 100 : 0;
  }, [taxPct]);

  const numericPto = useMemo(() => {
    const v = parseFloat(ptoDays);
    return Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0;
  }, [ptoDays]);

  const numericHealth = useMemo(() => {
    const v = parseFloat(healthDefault);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [healthDefault]);

  const numericExtras = useMemo(() => {
    const v = parseFloat(extrasDefault);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }, [extrasDefault]);

  const previewSalary = 60000;
  const previewCost = useMemo(() => {
    const draftSettings: PayrollSettings = {
      ...initial,
      sepIraRate: numericSep,
      employerTaxRate: numericTax,
      defaultPtoDaysPerYear: numericPto,
      defaultHealthReimbursementMonthly: numericHealth,
      defaultMonthlyEmployeeExtras: numericExtras,
      defaultWorkState,
    };
    return positionMonthlyCost(
      {
        id: 'preview',
        title: 'Preview',
        department: 'office',
        status: 'planned',
        payType: 'salary',
        annualSalary: previewSalary,
        healthReimbursementMonthly: numericHealth,
        ptoDaysPerYear: numericPto,
        monthlyEmployeeExtras: numericExtras,
        workState: defaultWorkState,
        startDate: new Date(),
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'preview',
      },
      new Date(),
      draftSettings,
    );
  }, [initial, numericSep, numericTax, numericPto, numericHealth, numericExtras, defaultWorkState]);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await onSave({
        sepIraRate: numericSep,
        employerTaxRate: numericTax,
        defaultPtoDaysPerYear: numericPto,
        defaultHealthReimbursementMonthly: numericHealth,
        defaultMonthlyEmployeeExtras: numericExtras,
        defaultWorkState,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Payroll defaults"
      >
        <div className="fin-modal-header">
          <h3>Payroll defaults</h3>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <div className="fin-modal-error">{error}</div>}

          <p className="fin-modal-help">
            These company-wide defaults apply to every payroll position unless that
            position sets a specific override. Employer payroll taxes are itemized
            (SS, Medicare, FUTA, SUTA) using each position&apos;s work state.
          </p>

          <fieldset className="fin-modal-fieldset">
            <legend>Retirement & location</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="settings-sep">SEP IRA %</label>
                <input
                  id="settings-sep"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={sepPct}
                  onChange={e => setSepPct(e.target.value)}
                  autoFocus
                />
                <span className="fin-modal-help">% of gross salary contributed each month.</span>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="settings-work-state">Default work state</label>
                <select
                  id="settings-work-state"
                  className="fin-modal-select"
                  value={defaultWorkState}
                  onChange={e => setDefaultWorkState(e.target.value)}
                >
                  {US_STATE_CODES.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
                <span className="fin-modal-help">
                  Used for SUTA when a position has no work state or linked user location.
                </span>
              </div>

              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="settings-tax">Legacy employer tax %</label>
                <input
                  id="settings-tax"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="0.05"
                  value={taxPct}
                  onChange={e => setTaxPct(e.target.value)}
                />
                <span className="fin-modal-help">
                  Only used when a position enables &quot;Override employer tax %&quot;. Forecast otherwise uses itemized payroll taxes.
                </span>
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>PTO & benefits defaults</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="settings-pto">PTO days / yr</label>
                <input
                  id="settings-pto"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="1"
                  value={ptoDays}
                  onChange={e => setPtoDays(e.target.value)}
                />
                <span className="fin-modal-help">
                  Additive backfill: each PTO day adds one extra day of base pay per month (annual ÷ 260).
                </span>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="settings-health">Health reimburse $/mo</label>
                <input
                  id="settings-health"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="25"
                  value={healthDefault}
                  onChange={e => setHealthDefault(e.target.value)}
                />
                <span className="fin-modal-help">Default per-position monthly reimbursement (taxable).</span>
              </div>

              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="settings-extras">Monthly extras default $</label>
                <input
                  id="settings-extras"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="10"
                  value={extrasDefault}
                  onChange={e => setExtrasDefault(e.target.value)}
                />
                <span className="fin-modal-help">
                  Per-position catch-all (car allowance, custom phone plan, etc.).
                </span>
              </div>
            </div>
          </fieldset>

          <div className="fin-cost-breakdown" aria-live="polite">
            <div className="fin-cost-breakdown-title">Preview — $60,000 position / mo ({defaultWorkState})</div>
            <PayrollCostBreakdown
              cost={previewCost}
              sepRateLabel={`${(numericSep * 100).toFixed(2)}%`}
              ptoDays={numericPto}
            />
          </div>
        </div>

        <div className="fin-modal-footer">
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="fin-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollSettingsModal;
